// frontend/app/components/admin/LessonUnlockDialog.js
//
// Confirms handing a locked lesson back to the seeder.
//
// The consequence is not reversible and is not visible at the moment it lands:
// the edits survive until somebody, possibly somebody else, presses Seed. So
// it is spelled out here in full and gated behind an acknowledgement, rather
// than left as a second "Are you sure?" that reads like every other one.

import React, { useState } from "react";
import { Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

const LessonUnlockDialog = ({ colors, visible, lessonTitle, busy, error, onCancel, onConfirm }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const close = () => {
    if (busy) return;
    setAcknowledged(false);
    onCancel();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={close}>
      <View style={styles.backdrop}>
        <View
          style={[
            styles.dialog,
            { backgroundColor: colors.panelBg, borderColor: colors.border },
          ]}
        >
          <View style={styles.titleRow}>
            <MaterialCommunityIcons name="lock-open-variant" size={19} color={colors.warning} />
            <Text style={[styles.title, { color: colors.textPrimary }]}>
              Hand this lesson back to the seeder?
            </Text>
          </View>

          <Text style={[styles.body, { color: colors.textSecondary }]}>
            <Text style={{ fontWeight: "700" }}>{lessonTitle}</Text> is locked because it
            was edited here, so seeding currently skips it.
          </Text>

          <View
            style={[
              styles.warning,
              {
                backgroundColor: colors.teacherTipBg,
                borderColor: colors.teacherTipBorder,
              },
            ]}
          >
            <Text style={[styles.warningText, { color: colors.teacherTipText }]}>
              Unlocking means the next ordinary “Seed lesson plans” rewrites this lesson
              from the file on the server. Every edit made here is discarded — the title,
              the step text, the blocks. The file has no copy of them, so there is nothing
              to restore from afterwards.
            </Text>
            <Text style={[styles.warningText, { color: colors.teacherTipText }]}>
              Nothing changes until that seed runs, and it may be run by someone else.
              Keeping the lock is what holds the edit; a forced seed overwrites it either
              way.
            </Text>
          </View>

          <TouchableOpacity
            onPress={() => setAcknowledged(!acknowledged)}
            disabled={busy}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: acknowledged }}
            accessibilityLabel="I understand the next seed will discard these edits"
            style={styles.ackRow}
          >
            <MaterialCommunityIcons
              name={acknowledged ? "checkbox-marked" : "checkbox-blank-outline"}
              size={19}
              color={acknowledged ? colors.accentPrimary : colors.textMuted}
            />
            <Text style={[styles.ackText, { color: colors.textSecondary }]}>
              I understand the next seed will discard these edits.
            </Text>
          </TouchableOpacity>

          {error ? (
            <Text style={[styles.error, { color: colors.danger }]} selectable>
              {error}
            </Text>
          ) : null}

          <View style={styles.actions}>
            <Pressable onPress={close} disabled={busy} accessibilityRole="button">
              <Text style={[styles.cancel, { color: colors.textSecondary, opacity: busy ? 0.4 : 1 }]}>
                Keep it locked
              </Text>
            </Pressable>

            <TouchableOpacity
              onPress={onConfirm}
              disabled={!acknowledged || busy}
              accessibilityRole="button"
              accessibilityState={{ disabled: !acknowledged || busy }}
              style={[
                styles.confirm,
                {
                  backgroundColor: colors.danger,
                  opacity: !acknowledged || busy ? 0.4 : 1,
                },
              ]}
            >
              <Text style={styles.confirmText}>
                {busy ? "Unlocking…" : "Unlock and allow overwrite"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  dialog: {
    width: "100%",
    maxWidth: 460,
    borderRadius: 14,
    borderWidth: 1,
    padding: 18,
  },
  titleRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
  title: { fontSize: 15, fontWeight: "800", flex: 1 },
  body: { fontSize: 13, lineHeight: 20, marginBottom: 12 },

  warning: { borderRadius: 10, borderWidth: 1, padding: 12, marginBottom: 12 },
  warningText: { fontSize: 12, lineHeight: 19 },

  ackRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 },
  ackText: { fontSize: 12, flex: 1, lineHeight: 18 },

  error: { fontSize: 12, lineHeight: 18, marginBottom: 10 },

  actions: { flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 16 },
  cancel: { fontSize: 13, fontWeight: "700" },
  confirm: { borderRadius: 9, paddingHorizontal: 14, paddingVertical: 9 },
  confirmText: { color: "#fff", fontSize: 12, fontWeight: "800" },
});

export default LessonUnlockDialog;
